// ═══════════════════════════════════════════════════════════════════════════════
// PWA inner-scroll: scroll focused fields above the software keyboard (iOS PWA).
// Does not change shell height or bottom nav visibility.
// ═══════════════════════════════════════════════════════════════════════════════

import { BOTTOM_NAV_SCROLL_CLEARANCE_PX } from './layoutChrome';
import { getVisualViewportBottomPx, isVirtualKeyboardOpen } from './pwaShellHeight';
import { isIosMobilePwa } from './pwaRuntime';
const FOCUS_SCROLL_BOTTOM_GAP_PX = 16;
const FOCUS_SCROLL_TOP_INSET_PX = 72;
const FOCUS_SCROLL_TOP_INSET_KEYBOARD_PX = 56;

const KEYBOARD_SETTLE_DELAYS_MS = [100, 300] as const;

export const isFocusableFormField = (element: HTMLElement): boolean => {
  if (element instanceof HTMLInputElement) {
    const inputType = element.type.toLowerCase();
    if (inputType === 'hidden' || inputType === 'checkbox' || inputType === 'radio' || inputType === 'file') {
      return false;
    }
    return !element.readOnly && !element.disabled;
  }

  if (element instanceof HTMLTextAreaElement) {
    return !element.readOnly && !element.disabled;
  }

  if (element instanceof HTMLSelectElement) {
    return !element.disabled;
  }

  if (element.isContentEditable) {
    return true;
  }

  return false;
};

/** iOS PWA: skip auto focus-scroll for textarea (handled by textarea keyboard recovery). */
export const shouldUsePwaFocusScrollForField = (element: HTMLElement): boolean => {
  if (!isFocusableFormField(element)) {
    return false;
  }
  if (element instanceof HTMLTextAreaElement && isIosMobilePwa()) {
    return false;
  }
  return true;
};

const resolveFocusScrollBottomGapPx = (): number => {
  if (isVirtualKeyboardOpen()) {
    return BOTTOM_NAV_SCROLL_CLEARANCE_PX + FOCUS_SCROLL_BOTTOM_GAP_PX;
  }
  return FOCUS_SCROLL_BOTTOM_GAP_PX;
};

/** Scroll a focused field into the visible area above the keyboard / bottom nav. */
export const scrollFormFieldIntoView = (
  scrollRoot: HTMLElement,
  target: HTMLElement,
): void => {
  const topInsetPx = isVirtualKeyboardOpen()
    ? FOCUS_SCROLL_TOP_INSET_KEYBOARD_PX
    : FOCUS_SCROLL_TOP_INSET_PX;
  const bottomGapPx = resolveFocusScrollBottomGapPx();
  const visibleBottom = getVisualViewportBottomPx() - bottomGapPx;
  const targetRect = target.getBoundingClientRect();

  if (targetRect.bottom > visibleBottom) {
    scrollRoot.scrollTop += targetRect.bottom - visibleBottom;
  }

  const targetRectAfter = target.getBoundingClientRect();
  const visibleTop = scrollRoot.getBoundingClientRect().top + topInsetPx;
  if (targetRectAfter.top < visibleTop) {
    scrollRoot.scrollTop -= visibleTop - targetRectAfter.top;
  }
};

export const scheduleFormFieldFocusScroll = (
  scrollRoot: HTMLElement,
  target: HTMLElement,
): void => {
  requestAnimationFrame(() => {
    scrollFormFieldIntoView(scrollRoot, target);
    KEYBOARD_SETTLE_DELAYS_MS.forEach((delayMs) => {
      window.setTimeout(() => {
        scrollFormFieldIntoView(scrollRoot, target);
      }, delayMs);
    });
  });
};
